import { z } from 'zod';
/** TechSpec §3.2 / §6.1 — sources and the connectors that back them. */
export const SOURCE_MODES = ['poll', 'webhook', 'stream'];
export const SOURCE_STATUSES = ['active', 'degraded', 'paused', 'error'];
export const WEBHOOK_AUTH_MODES = ['hmac', 'shared-secret'];
/** Minimum poll interval (PRD VS-011). */
export const MIN_POLL_INTERVAL_S = 10;
/** A source as the API serves it. Secrets are never part of it, not even redacted (§13.2). */
export const SourceSchema = z.object({
    id: z.uuid(),
    connectorId: z.string(),
    name: z.string(),
    mode: z.enum(SOURCE_MODES),
    config: z.record(z.string(), z.unknown()),
    status: z.enum(SOURCE_STATUSES),
    enabled: z.boolean(),
    pollIntervalS: z.number().int().nullable(),
    /** Path of the webhook endpoint, e.g. /api/v1/webhooks/<slug>. Not a secret on its own. */
    webhookPath: z.string().nullable(),
    lastRunAt: z.iso.datetime({ offset: true }).nullable(),
    lastSuccessAt: z.iso.datetime({ offset: true }).nullable(),
    lastError: z.string().nullable(),
    consecutiveFailures: z.number().int(),
    createdAt: z.iso.datetime({ offset: true }),
});
export const SourceListResponseSchema = z.object({ sources: z.array(SourceSchema) });
export const CreateSourceRequestSchema = z.object({
    connectorId: z.string().min(1).max(64),
    name: z.string().trim().min(1).max(120),
    config: z.record(z.string(), z.unknown()).default({}),
    /** Write-only. Stored encrypted; never returned. */
    secrets: z.record(z.string(), z.string().max(4096)).default({}),
    pollIntervalS: z.number().int().min(MIN_POLL_INTERVAL_S).max(86_400).optional(),
});
export const CreateSourceResponseSchema = z.object({
    source: SourceSchema,
    /**
     * Webhook sources only, and only in this response: the secret the sender
     * must present. It is stored encrypted and can never be read back — like
     * the TOTP secret at enrolment.
     */
    webhookSecret: z.string().optional(),
});
export const UpdateSourceRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    config: z.record(z.string(), z.unknown()),
    secrets: z.record(z.string(), z.string().max(4096)),
    pollIntervalS: z.number().int().min(MIN_POLL_INTERVAL_S).max(86_400),
    enabled: z.boolean(),
})
    .partial();
export const TestRunResponseSchema = z.object({
    ok: z.boolean(),
    durationMs: z.number(),
    /** What the run would have emitted. Nothing was persisted. */
    events: z.number().int(),
    metrics: z.number().int(),
    sample: z.array(z.record(z.string(), z.unknown())).max(10),
    error: z.string().nullable(),
});
export const ConnectorSchema = z.object({
    id: z.string(),
    name: z.string(),
    mode: z.enum(SOURCE_MODES),
    webhookAuth: z.enum(WEBHOOK_AUTH_MODES).nullable(),
    secretKeys: z.array(z.string()),
    defaultPollIntervalS: z.number().int().nullable(),
    /** JSON Schema of the config, for the UI form. */
    configSchema: z.record(z.string(), z.unknown()),
    /**
     * Names the connector can look up for the form (step 36a, PRD VS-025): a config property whose
     * JSON Schema carries `"x-lookup": "<name>"` is picked from `GET /connectors/:id/lookups/:name`.
     * For an array property, an option's `value` is one item of that array.
     */
    lookups: z.array(z.string()),
});
/** One choice offered by a connector lookup. `value` is whatever the config field stores. */
export const LookupOptionSchema = z.object({
    value: z.unknown(),
    label: z.string().min(1).max(120),
    description: z.string().max(200).nullable(),
});
export const LookupQuerySchema = z.object({
    q: z.string().trim().max(100).default(''),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const LookupResponseSchema = z.object({ options: z.array(LookupOptionSchema) });
export const ConnectorListResponseSchema = z.object({ connectors: z.array(ConnectorSchema) });
/** Items ingested per source over the last 24 h (PRD VS-015). */
export const SourceStatsResponseSchema = z.object({
    stats: z.array(z.object({
        sourceId: z.uuid(),
        events24h: z.number().int(),
        metrics24h: z.number().int(),
    })),
});
/** A polled source with no success for this many intervals is stale (PRD VS-015). */
export const STALE_AFTER_INTERVALS = 3;
/**
 * Stale = a polled source that has not succeeded for 3× its interval. Webhook
 * sources have no interval — silence is normal — so they are never stale; their
 * failures show as status (PRD VS-015 note). The one place this rule lives.
 */
export function isSourceStale(source, now) {
    if (source.mode !== 'poll' || !source.enabled || !source.pollIntervalS)
        return false;
    const since = new Date(source.lastSuccessAt ?? source.createdAt).getTime();
    return now - since > STALE_AFTER_INTERVALS * source.pollIntervalS * 1000;
}
//# sourceMappingURL=sources.js.map